import { ReloadOutlined } from '@ant-design/icons';
import {
  type IReportProductMetaDescription,
  type ITranslation,
} from '@mahawi/eshop-common/src/types';
import { Button, Divider, Select, Space, Spin, Switch } from 'antd';
import { type ColumnsType } from 'antd/es/table';
import React, { useEffect, useState } from 'react';
import { useSelector } from 'react-redux';

import DetailPageHeader from '../components/detail-page-header';
import Link from '../components/link';
import Table from '../components/table';
import {
  AutomatProgressBar,
  type IAutomatProgress,
} from '../fragments/automat-progress-bar';
import { openGoogleTranslateLink } from '../fragments/google-translate-link';
import { useAppDispatch } from '../hooks/use-app-dispatch';
import { useAutomatDocumentTitle } from '../hooks/use-automat-document-title';
import { type AppDispatch, type RootState } from '../reducers';
import { namesDescriptionsSlugsUpdate } from '../reducers/product/reducer';
import { reportProductMetaDescriptionLoad } from '../reducers/report/reducer';
import { SPACING } from '../theme/tokens';

const DELAY_OPTIONS = Array.from({ length: 21 }, (_, i) => ({
  value: i * 100,
  label: `${i * 100} ms`,
}));

interface DataType extends IReportProductMetaDescription {
  key: string;
}

async function startAutomat(
  dataSource: DataType[],
  dispatch: AppDispatch,
  setRunning: React.Dispatch<React.SetStateAction<boolean>>,
  delay: number,
  setProgress: React.Dispatch<React.SetStateAction<IAutomatProgress | null>>,
): Promise<void> {
  // A language can only receive a meta description if the product already
  // has a base translation (name/description/slug) there — updateProductLanguages()
  // has nothing to attach a meta description to otherwise. missingCodes from
  // the report includes every active language lacking a meta description,
  // regardless of whether the product has a page there at all, so filter to
  // codes that already have a name.
  const translatableMissingCodes = (record: DataType): string[] => {
    const existingNameCodes = new Set(
      record.translation.names.map((n) => n.code),
    );

    return record.languageType.missingCodes.filter((code) =>
      existingNameCodes.has(code),
    );
  };

  let total = 0;
  for (const record of dataSource) {
    const [src] = record.translation.metaDescriptions;
    if (src && src.value.length >= 2) {
      total += translatableMissingCodes(record).length;
    }
  }

  setRunning(true);
  const startedAt = Date.now();
  setProgress({ current: 0, total, startedAt });
  let current = 0;

  for (const record of dataSource) {
    const metaDescriptions: ITranslation[] = [];
    const [sourceMetaDescription] = record.translation.metaDescriptions;

    for (const languageTypeCodeMissing of translatableMissingCodes(record)) {
      if (sourceMetaDescription && sourceMetaDescription.value.length >= 2) {
        await new Promise<void>((resolve): void => {
          window.addEventListener(
            'focus',
            async (): Promise<void> => {
              const text: string = await navigator.clipboard.readText();
              const tt: string = text.trim();

              if (tt) {
                metaDescriptions.push({
                  code: languageTypeCodeMissing,
                  value: tt,
                });
              }

              await navigator.clipboard.writeText('');

              resolve();
            },
            { once: true },
          );

          openGoogleTranslateLink(
            sourceMetaDescription.code,
            languageTypeCodeMissing,
            sourceMetaDescription.value,
          );
        });

        current++;
        setProgress({ current, total, startedAt });

        if (delay > 0) {
          await new Promise<void>((resolve) => setTimeout(resolve, delay));
        }
      }
    }

    if (metaDescriptions.length > 0) {
      dispatch(
        namesDescriptionsSlugsUpdate(
          record.product.uuid,
          [],
          [],
          [],
          metaDescriptions,
          [],
        ),
      );
    }
  }

  setRunning(false);
  setProgress(null);
}

function ReportProductMetaDescription(): React.ReactElement {
  const dispatch = useAppDispatch();
  const reportProductMetaDescription = useSelector(
    (s: RootState) => s.Report.productMetaDescription,
  );

  const [dataSource, setDataSource] = useState<DataType[]>([]);
  const [columns, setColumns] = useState<ColumnsType<object>>([]);
  const [isRunning, setRunning] = useState<boolean>(false);
  const [delay, setDelay] = useState<number>(100);
  const [progress, setProgress] = useState<IAutomatProgress | null>(null);
  const [onlyActiveLanguages, setOnlyActiveLanguages] = useState<boolean>(true);

  useEffect((): void => {
    if (!reportProductMetaDescription) {
      return;
    }

    const columnsUE: ColumnsType<object> = [
      {
        title: 'UUID',
        dataIndex: ['product', 'uuid'],
        render: (_text, record: DataType): React.ReactElement => (
          <Link
            to={`/product/${record.product.uuid}`}
            openInNewTab
            label={record.product.uuid}
          />
        ),
      },
      {
        title: 'Meta description',
        dataIndex: ['translation', 'metaDescriptions'],
        render: (_text, record: DataType): string =>
          record.translation.metaDescriptions
            .map((t) => `[${t.code}] ${t.value}`)
            .join(' | '),
      },
      {
        title: 'Missing meta description',
        dataIndex: ['languageType', 'missingCodes'],
        render: (_text, record: DataType): string =>
          record.languageType.missingCodes.join(', '),
      },
    ];

    setColumns(columnsUE);

    const dataSourceUE = reportProductMetaDescription.map((item) => ({
      ...item,
      key: item.product.uuid,
    }));

    setDataSource(dataSourceUE);
  }, [reportProductMetaDescription]);

  useAutomatDocumentTitle(isRunning, progress);

  return (
    <>
      <DetailPageHeader title="Report product meta description" />

      <Space size={SPACING.lg} align="start">
        <Switch
          checkedChildren="Active languages"
          unCheckedChildren="All languages"
          checked={onlyActiveLanguages}
          onChange={setOnlyActiveLanguages}
        />
        <Select
          value={delay}
          onChange={setDelay}
          options={DELAY_OPTIONS}
          style={{ width: 110 }}
          disabled={isRunning}
        />
        <Button
          type="primary"
          onClick={(): void => {
            dispatch(reportProductMetaDescriptionLoad(onlyActiveLanguages));
          }}
          disabled={!reportProductMetaDescription}
        >
          <ReloadOutlined />
        </Button>
        <Button
          type="primary"
          onClick={(): void => {
            startAutomat(dataSource, dispatch, setRunning, delay, setProgress);
          }}
          disabled={
            !reportProductMetaDescription ||
            isRunning ||
            dataSource.length === 0
          }
        >
          GT Automat
        </Button>
        <Spin size="small" spinning={isRunning} />
      </Space>

      <AutomatProgressBar isRunning={isRunning} progress={progress} />

      <Divider />

      <Table
        columns={columns}
        dataSource={dataSource}
        isLoading={!reportProductMetaDescription}
      />
    </>
  );
}

export default ReportProductMetaDescription;
